import { createSlice } from "@reduxjs/toolkit";
import {
  getWarehouses,
  addNewWarehouse,
  updateWarehouse,
  deleteWarehouse,
  getWarehousesCompanies,
  getWarehousesWithdraw,
  addNewWarehouseWithdraw,
  updateWarehouseWithdraw,
  deleteWarehouseWithdraw,
  getWarehousesReportSummary,
  getWarehousesReportInvoice,
  getWarehousesInvoices,
  getWarehousesInvoiceByNumber,
  addNewWarehousesInvoice,
  updateWarehousesInvoice,
  deleteWarehousesInvoice,
} from "./thunk";

export const initialState = {
  warehouses: [],
  errorWarehouses: {},
  isLoadingWarehouses: false,
  isWarehousesSuccess: false,
  isWarehousesFailed: false,

  warehousesCompanies: [],
  errorWarehousesCompanies: {},
  isLoadingWarehousesCompanies: false,
  isWarehousesCompaniesSuccess: false,
  isWarehousesCompaniesFailed: false,

  warehousesWithdraw: [],
  errorWarehousesWithdraw: {},
  isLoadingWarehousesWithdraw: false,
  isWarehousesWithdrawSuccess: false,
  isWarehousesWithdrawFailed: false,

  warehousesReportSummary: [],
  errorWarehousesReportSummary: {},
  isLoadingWarehousesReportSummary: false,
  isWarehousesReportSummarySuccess: false,
  isWarehousesReportSummaryFailed: false,

  warehousesReportInvoice: [],
  errorWarehousesReportInvoice: {},
  isLoadingWarehousesReportInvoice: false,
  isWarehousesReportInvoiceSuccess: false,
  isWarehousesReportInvoiceFailed: false,

  warehousesInvoices: [],
  warehousesInvoicesError: {},
  isWarehousesInvoicesLoading: false,
  isWarehousesInvoicesSuccess: false,
  isWarehousesInvoicesFailed: false,

  warehousesInvoicesSpecial: [],
  isWarehousesInvoicesSpecialLoading: false,
  isWarehousesInvoicesSpecialSuccess: false,
  isWarehousesInvoicesSpecialFail: false,
};

const warehouseSlice = createSlice({
  name: "warehouse",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    //==================================[ /getWarehouses/ ]=======================================
    builder.addCase(getWarehouses.pending, (state) => {
      state.isLoadingWarehouses = true;
      state.isWarehousesSuccess = false;
      state.errorWarehouses = null;
    });

    builder.addCase(getWarehouses.fulfilled, (state, action) => {
      state.isLoadingWarehouses = false;
      state.isWarehousesSuccess = true;
      state.warehouses = Array.isArray(action.payload)
        ? action.payload
        : action.payload ?? [];
    });

    builder.addCase(getWarehouses.rejected, (state, action) => {
      state.isLoadingWarehouses = false;
      state.isWarehousesSuccess = false;
      state.errorWarehouses = action.error.message;
    });

    builder.addCase(addNewWarehouse.fulfilled, (state, action) => {
      state.warehouses.push(action.payload);
      state.isWarehouseAdd = true;
      state.isWarehouseAddFail = false;
    });

    builder.addCase(addNewWarehouse.rejected, (state, action) => {
      state.errorWarehouses = action.payload.error || null;
      state.isWarehouseAdd = false;
      state.isWarehouseAddFail = true;
    });

    builder.addCase(updateWarehouse.fulfilled, (state, action) => {
      if (!action.payload?._id) {
        console.warn("updateWarehouse returned undefined or missing _id");
        return;
      }

      state.warehouses = state.warehouses.map((warehouse) => {
        if (!warehouse?._id) return warehouse;
        return warehouse._id.toString() === action.payload._id.toString()
          ? { ...warehouse, ...action.payload }
          : warehouse;
      });

      state.isWarehouseUpdate = true;
      state.isWarehouseUpdateFail = false;
    });

    builder.addCase(updateWarehouse.rejected, (state, action) => {
      state.errorWarehouses = action.payload.error || null;
      state.isWarehouseUpdate = false;
      state.isWarehouseUpdateFail = true;
    });

    builder.addCase(deleteWarehouse.fulfilled, (state, action) => {
      state.warehouses = (state.warehouses || []).filter(
        (warehouse) => warehouse._id.toString() !== action.payload.id.toString()
      );

      state.isWarehouseDelete = true;
      state.isWarehouseDeleteFail = false;
    });

    builder.addCase(deleteWarehouse.rejected, (state, action) => {
      state.errorWarehouses = action.payload.error || null;
      state.isWarehouseDelete = false;
      state.isWarehouseDeleteFail = true;
    });

    //==================================[ /getWarehousesCompanies/ ]=======================================
    builder.addCase(getWarehousesCompanies.pending, (state) => {
      state.isLoadingWarehousesCompanies = true;
      state.isWarehousesCompaniesSuccess = false;
      state.errorWarehousesCompanies = null;
    });

    builder.addCase(getWarehousesCompanies.fulfilled, (state, action) => {
      state.isLoadingWarehousesCompanies = false;
      state.isWarehousesCompaniesSuccess = true;
      state.warehousesCompanies = Array.isArray(action.payload)
        ? action.payload
        : action.payload ?? [];
    });

    builder.addCase(getWarehousesCompanies.rejected, (state, action) => {
      state.isLoadingWarehousesCompanies = false;
      state.isWarehousesCompaniesSuccess = false;
      state.errorWarehousesCompanies = action.error.message;
    });

    //==================================[ /getWarehousesWithdraw/ ]=======================================
    builder.addCase(getWarehousesWithdraw.pending, (state) => {
      state.isLoadingWarehousesWithdraw = true;
      state.isWarehousesWithdrawSuccess = false;
      state.errorWarehousesWithdraw = null;
    });

    builder.addCase(getWarehousesWithdraw.fulfilled, (state, action) => {
      state.isLoadingWarehousesWithdraw = false;
      state.isWarehousesWithdrawSuccess = true;
      state.warehousesWithdraw = Array.isArray(action.payload)
        ? action.payload
        : action.payload ?? [];
    });

    builder.addCase(getWarehousesWithdraw.rejected, (state, action) => {
      state.isLoadingWarehousesWithdraw = false;
      state.isWarehousesWithdrawSuccess = false;
      state.errorWarehousesWithdraw = action.error.message;
    });

    builder.addCase(addNewWarehouseWithdraw.fulfilled, (state, action) => {
      state.warehousesWithdraw.push(action.payload);
      state.isWarehouseWithdrawAdd = true;
      state.isWarehouseWithdrawAddFail = false;
    });

    builder.addCase(addNewWarehouseWithdraw.rejected, (state, action) => {
      state.errorWarehousesWithdraw = action.payload.error || null;
      state.isWarehouseWithdrawAdd = false;
      state.isWarehouseWithdrawAddFail = true;
    });

    builder.addCase(updateWarehouseWithdraw.fulfilled, (state, action) => {
      if (!action.payload?._id) {
        console.warn("updateWarehouse returned undefined or missing _id");
        return;
      }

      state.warehousesWithdraw = state.warehousesWithdraw.map((warehouse) => {
        if (!warehouse?._id) return warehouse;
        return warehouse._id.toString() === action.payload._id.toString()
          ? { ...warehouse, ...action.payload }
          : warehouse;
      });

      state.isWarehouseWithdrawUpdate = true;
      state.isWarehouseWithdrawUpdateFail = false;
    });

    builder.addCase(updateWarehouseWithdraw.rejected, (state, action) => {
      state.errorWarehousesWithdraw = action.payload.error || null;
      state.isWarehouseWithdrawUpdate = false;
      state.isWarehouseWithdrawUpdateFail = true;
    });

    builder.addCase(deleteWarehouseWithdraw.fulfilled, (state, action) => {
      state.warehousesWithdraw = (state.warehousesWithdraw || []).filter(
        (warehouse) => warehouse._id.toString() !== action.payload.id.toString()
      );

      state.isWarehouseWithdrawDelete = true;
      state.isWarehouseWithdrawDeleteFail = false;
    });

    builder.addCase(deleteWarehouseWithdraw.rejected, (state, action) => {
      state.errorWarehousesWithdraw = action.payload.error || null;
      state.isWarehouseWithdrawDelete = false;
      state.isWarehouseWithdrawDeleteFail = true;
    });

    //==================================[ /getWarehousesReportSummary/ ]=======================================
    builder.addCase(getWarehousesReportSummary.pending, (state) => {
      state.isLoadingWarehousesReportSummary = true;
      state.isWarehousesReportSummarySuccess = false;
      state.errorWarehousesReportSummary = null;
    });

    builder.addCase(getWarehousesReportSummary.fulfilled, (state, action) => {
      state.isLoadingWarehousesReportSummary = false;
      state.isWarehousesReportSummarySuccess = true;
      state.warehousesReportSummary = Array.isArray(action.payload)
        ? action.payload
        : action.payload ?? [];
    });

    builder.addCase(getWarehousesReportSummary.rejected, (state, action) => {
      state.isLoadingWarehousesReportSummary = false;
      state.isWarehousesReportSummarySuccess = false;
      state.errorWarehousesReportSummary = action.error.message;
    });

    //==================================[ /getWarehousesReportInvoice/ ]=======================================
    builder.addCase(getWarehousesReportInvoice.pending, (state) => {
      state.isLoadingWarehousesReportInvoice = true;
      state.isWarehousesReportInvoiceSuccess = false;
      state.errorWarehousesReportInvoice = null;
    });

    builder.addCase(getWarehousesReportInvoice.fulfilled, (state, action) => {
      state.isLoadingWarehousesReportInvoice = false;
      state.isWarehousesReportInvoiceSuccess = true;
      state.warehousesReportInvoice = Array.isArray(action.payload)
        ? action.payload
        : action.payload ?? [];
    });

    builder.addCase(getWarehousesReportInvoice.rejected, (state, action) => {
      state.isLoadingWarehousesReportInvoice = false;
      state.isWarehousesReportInvoiceSuccess = false;
      state.errorWarehousesReportInvoice = action.error.message;
    });

    //==================================[ /getWarehousesInvoices/ ]=======================================
    builder.addCase(getWarehousesInvoices.pending, (state) => {
      state.isWarehousesInvoicesLoading = true;
      state.isWarehousesInvoicesSuccess = false;
      state.warehousesInvoicesError = null;
    });
    builder.addCase(getWarehousesInvoices.fulfilled, (state, action) => {
      state.isWarehousesInvoicesLoading = false;
      state.isWarehousesInvoicesSuccess = true;
      state.warehousesInvoices = action.payload;
    });
    builder.addCase(getWarehousesInvoices.rejected, (state, action) => {
      state.isWarehousesInvoicesLoading = false;
      state.isWarehousesInvoicesSuccess = false;
      state.warehousesInvoicesError = action.error.message;
    });

    builder.addCase(addNewWarehousesInvoice.fulfilled, (state, action) => {
      state.warehousesInvoices.push(action.payload);
      state.isWarehousesInvoiceAdd = true;
      state.isWarehousesInvoiceAddFail = false;
    });

    builder.addCase(addNewWarehousesInvoice.rejected, (state, action) => {
      state.warehousesInvoicesError = action.payload.error || null;
      state.isWarehousesInvoiceAdd = false;
      state.isWarehousesInvoiceAddFail = true;
    });

    builder.addCase(updateWarehousesInvoice.fulfilled, (state, action) => {
      state.warehousesInvoices = state.warehousesInvoices.map((invoice) =>
        invoice._id.toString() === action.payload._id.toString()
          ? { ...invoice, ...action.payload }
          : invoice
      );
      state.isWarehousesInvoiceUpdate = true;
      state.isWarehousesInvoiceUpdateFail = false;
    });

    builder.addCase(updateWarehousesInvoice.rejected, (state, action) => {
      state.warehousesInvoicesError = action.payload.error || null;
      state.isWarehousesInvoiceUpdate = false;
      state.isWarehousesInvoiceUpdateFail = true;
    });

    builder.addCase(deleteWarehousesInvoice.fulfilled, (state, action) => {
      state.warehousesInvoices = (state.warehousesInvoices || []).filter(
        (invoice) => invoice._id.toString() !== action.payload.id.toString()
      );

      state.isWarehousesInvoiceDelete = true;
      state.isWarehousesInvoiceDeleteFail = false;
    });

    builder.addCase(deleteWarehousesInvoice.rejected, (state, action) => {
      state.error = action.payload.error || null;
      state.isWarehousesInvoiceDelete = false;
      state.isWarehousesInvoiceDeleteFail = true;
    });

    builder.addCase(getWarehousesInvoiceByNumber.pending, (state) => {
      state.isWarehousesInvoicesSpecialLoading = true;
      state.isWarehousesInvoicesSpecialSuccess = false;
      state.warehousesInvoicesError = null;
    });
    builder.addCase(getWarehousesInvoiceByNumber.fulfilled, (state, action) => {
      state.isWarehousesInvoicesSpecialLoading = false;
      state.isWarehousesInvoicesSpecialSuccess = true;
      state.warehousesInvoicesSpecial = action.payload;
    });
    builder.addCase(getWarehousesInvoiceByNumber.rejected, (state, action) => {
      state.isWarehousesInvoicesSpecialLoading = false;
      state.isWarehousesInvoicesSpecialSuccess = false;
      state.error = action.error.message;
    });
  },
});

export default warehouseSlice.reducer;
